import { Head } from "@inertiajs/react"
import { LandingFooter } from "@/components/landing/footer"
import { LandingHeader } from "@/components/landing/header"

interface PrivacyProps {
  effectiveDate: string
}

export default function Privacy({ effectiveDate }: PrivacyProps) {
  return (
    <>
      <Head title="Privacy Policy" />
      <div className="flex min-h-screen flex-col bg-background">
        <LandingHeader />
        <main className="mx-auto w-full max-w-3xl flex-1 px-6 py-16">
          <article className="prose prose-invert max-w-none">
            <h1>Privacy Policy</h1>
            <p className="text-sm text-muted-foreground">
              Effective date: {effectiveDate}
            </p>

            <p>
              AdWizard ("we", "us", or "the Service"), operated by Bitmapped
              Designs, is a tool that connects your Google Ads account to AI
              assistants such as Claude through the Model Context Protocol.
              This policy explains what information we collect, how we use
              it, and the choices you have.
            </p>

            <h2>Information we collect</h2>
            <ul>
              <li>
                <strong>Account information.</strong> When you sign in with
                Google we receive your name, email address, and a Google user
                ID.
              </li>
              <li>
                <strong>Google OAuth tokens.</strong> We store an encrypted
                refresh token so the Service can call the Google Ads API on
                your behalf. We do not see or store your Google password.
              </li>
              <li>
                <strong>Google Ads data.</strong> When you use the Service we
                fetch information from your Google Ads accounts (campaigns,
                ad groups, keywords, search terms, performance metrics, etc.)
                to return it to you. We do not retain this data beyond the
                lifespan of a request, except for short-term caches required
                for performance.
              </li>
              <li>
                <strong>Operational logs.</strong> We log API requests, error
                traces, and basic request metadata (timestamp, IP address,
                user agent) for security and debugging.
              </li>
            </ul>

            <h2>How we use your information</h2>
            <ul>
              <li>To authenticate you and authorize requests to Google Ads.</li>
              <li>
                To provide the tools and reports you ask for through the
                Model Context Protocol.
              </li>
              <li>
                To monitor reliability, prevent abuse, and improve the
                Service.
              </li>
              <li>To respond when you contact us for support.</li>
            </ul>

            <h2>Limited use of Google user data</h2>
            <p>
              The Service's use and transfer of information received from
              Google APIs adheres to the{" "}
              <a
                href="https://developers.google.com/terms/api-services-user-data-policy"
                target="_blank"
                rel="noreferrer"
              >
                Google API Services User Data Policy
              </a>
              , including the Limited Use requirements. Specifically:
            </p>
            <ul>
              <li>
                We use Google user data only to provide or improve
                user-facing features of the Service.
              </li>
              <li>
                We do not transfer Google user data to third parties except
                as necessary to provide or improve the Service, comply with
                applicable law, or as part of a merger, acquisition, or sale
                of assets with notice to users.
              </li>
              <li>
                We do not use Google user data for serving advertisements,
                including retargeted, personalized, or interest-based
                advertising.
              </li>
              <li>
                We do not allow humans to read Google user data unless we
                have your explicit consent for specific data, it is needed
                for security purposes, to comply with law, or the data has
                been aggregated and anonymized.
              </li>
            </ul>

            <h2>How we share information</h2>
            <p>
              We do not sell your personal information. We share information
              only with infrastructure vendors that operate the Service
              (hosting, error monitoring) under contractual confidentiality
              obligations, and with Google itself when we make API calls on
              your behalf.
            </p>

            <h2>Data retention</h2>
            <p>
              We retain your account record and OAuth tokens until you delete
              your account or revoke access from your Google account. We
              retain operational logs for up to 90 days. You can request
              deletion at any time by emailing us at the address below.
            </p>

            <h2>Your choices</h2>
            <ul>
              <li>
                <strong>Revoke access.</strong> You can disconnect AdWizard
                from your Google account at any time at{" "}
                <a
                  href="https://myaccount.google.com/permissions"
                  target="_blank"
                  rel="noreferrer"
                >
                  myaccount.google.com/permissions
                </a>
                . Your refresh token will stop working immediately.
              </li>
              <li>
                <strong>Delete your account.</strong> Email us at the address
                below and we will remove your account record and tokens
                within 30 days.
              </li>
              <li>
                <strong>Access &amp; correction.</strong> Email us to request
                a copy of the personal information we hold about you or to
                correct it.
              </li>
            </ul>

            <h2>Security</h2>
            <p>
              We encrypt OAuth refresh tokens at rest, transmit data over
              TLS, and follow industry practices for access control. No
              system is perfectly secure; report suspected vulnerabilities to
              the contact address below.
            </p>

            <h2>Children</h2>
            <p>
              The Service is not directed to children under 13 and we do not
              knowingly collect personal information from them.
            </p>

            <h2>Changes to this policy</h2>
            <p>
              We may update this policy from time to time. Material changes
              will be announced on this page with an updated effective date.
            </p>

            <h2>Contact</h2>
            <p>
              Questions, deletion requests, or privacy concerns:{" "}
              <a href="mailto:colleen@bitmappeddesigns.com">
                colleen@bitmappeddesigns.com
              </a>
              .
            </p>
          </article>
        </main>
        <LandingFooter />
      </div>
    </>
  )
}
